import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, computed, effect, OnDestroy, OnInit, Signal, Type, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AngularSplitModule } from 'angular-split';
import { TabsModule } from 'primeng/tabs';
import { map } from 'rxjs';
import { ChatComponent } from '../../components/chat/chat.component';
import { ExplorerComponent } from '../../components/explorer/explorer.component';
import { buildRootNode, Node } from '../../components/explorer/node';
import { ShellComponent } from '../../components/shell/shell.component';
import { ViewerComponent } from '../../components/viewer/viewer.component';
import { UsdmComponent } from '../../components/workflows/usdm/usdm.component';
import { AiFileWriterService } from '../../services/ai/ai-file-writer.service';
import { FileSystemService } from '../../services/file-system.service';
import { McpService } from '../../services/mcp.service';
import { OauthService } from '../../services/oauth.service';
import { UserSettingsService } from '../../services/user-settings.service';
import { WorkflowType } from '../../services/workflow.service';
import { WorkspaceTabsService } from '../../services/workspace-tabs.service';

@Component({
  selector: 'app-workspace',
  standalone: true,
  imports: [
    CommonModule,
    ExplorerComponent,
    ViewerComponent,
    ChatComponent,
    ShellComponent,
    AngularSplitModule,
    TabsModule
  ],
  templateUrl: './workspace.component.html',
  styleUrls: ['./workspace.component.css'],
})
export class WorkspaceComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ShellComponent) shellComponent!: ShellComponent;
  @ViewChild(ChatComponent) chatComponent!: ChatComponent;

  selectedNode = computed(() => this.fs.lastSelectedNode());

  sessionId: string;
  node: Node | null = null;
  visibleFilePath;
  split: Signal<number[]>;
  dynamicComponent: Type<unknown> | null = null;

  // Getter methods to access signals from the service
  get activeTab() {
    return this.tabs.activeTab();
  }

  get tabList() {
    return this.tabs.tabList();
  }

  constructor(
    private route: ActivatedRoute,
    private fs: FileSystemService,
    private userSettings: UserSettingsService,
    private mcpService: McpService,
    private oauth: OauthService,
    private tabs: WorkspaceTabsService,
    private cdr: ChangeDetectorRef,
    private bpObs: BreakpointObserver,
    private aiFileWriterService: AiFileWriterService,  // !!DO NOT REMOVE!! - initializes AI file writing subscriptions
  ) {
    this.sessionId = '';
    this.visibleFilePath = this.fs.visibleNode;
    this.split = computed(() => {
      console.log("Calculating split with tabs:", this.tabs.tabList(), this.tabs.activeTab());
      return (this.tabs.tabList().length > 1) ? [5, 95] : [0, 100];
    });
    effect(() => {
      const currentTab = this.tabs.activeTab();
      if (currentTab) {
        this.setDynamicComponent(currentTab.label);
      }
      this.cdr.detectChanges();
    });

    this.bpObs.observe([
      '(max-width: 1000px)'
    ])
      .pipe(
        map(state => state.matches),
      ).subscribe(isMobile => {
        if (isMobile) {
          window.location.href = '/';  // Redirect to home page on mobile view;
        }
      });
  }

  /** Exact-path file lookup, falling back on case-insensitive comparison. */
  private findFileIgnoreCase(root: Node, path: string): string | null {
    const want = path.toLowerCase();
    const stack: Node[] = [root];
    while (stack.length) {
      const n = stack.pop()!;
      if (!n.isFolder && n.path.toLowerCase() === want) return n.path;
      stack.push(...n.items);
    }
    return null;
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(() => {
      // Generate a new session ID if not provided
      this.sessionId = crypto.randomUUID();
    });

    // Get the node from the file system service
    // this.node.fetch(this.userSettings.serverUrl).then((node) => {
    //   this.node = node;
    //   this.fs.rootNode = node;
    // });
    this.userSettings.serverUrl$.subscribe((url) => {
      console.log('[workspace] serverUrl$ emitted:', url);
      if (url) {
        buildRootNode(url).then((node) => {
          console.log('[workspace] rootNode built:', node.path, 'items:', node.items.length);
          this.fs.rootNode = node;
          this.node = node;
          // Deep link: /workspace?open=<workspace-relative path> opens the file
          // in the viewer (used by the codegen results' View buttons).
          const open = this.route.snapshot.queryParams['open'];
          console.log('[workspace] open query param:', open, '| full queryParams:', this.route.snapshot.queryParams);
          if (open) {
            // The backend may record the file with different casing than it
            // has on disk (VS_derived.csv vs vs_derived.csv on a
            // case-insensitive filesystem) — fall back to a case-insensitive
            // match over the tree.
            const target = `${node.path}/${open}`;
            const resolved = this.findFileIgnoreCase(node, target) ?? target;
            console.log('[workspace] opening', resolved);
            this.fs.changeVisibleNode(resolved).catch((err) =>
              console.error(`[workspace] could not open ${open} in viewer:`, err)
            );
          }
        }).catch((err) => console.error('[workspace] buildRootNode failed:', err));
      }
    });

    this.fs.rootNodeRefresh$.subscribe((node) => {
      this.node = node;
    });

    this.oauth.getUser(true);
  }

  ngAfterViewInit(): void {
    // Add a delay to ensure components are properly rendered
    setTimeout(async () => {
      // Force a refresh of the split layout
      window.dispatchEvent(new Event('resize'));

      try {
        await this.mcpService.connect();
        // Check for pending message in localStorage
        const pendingMessageJson = localStorage.getItem('pendingMessage');
        const selectedWorkflowJSON = localStorage.getItem('selectedWorkflow');
        if (pendingMessageJson) {
          const pendingMessage = JSON.parse(pendingMessageJson);
          const selectedWorkflow: WorkflowType | null = selectedWorkflowJSON ? JSON.parse(selectedWorkflowJSON) : null;

          if (this.chatComponent) {
            // Send the pending message to the chat component
            this.chatComponent.msgInput = pendingMessage.content;
            if (selectedWorkflow) {
              this.chatComponent.updateWorkflow(selectedWorkflow);
            }
            this.chatComponent.handleSend(pendingMessage.content);
            // Clear the pending message
            localStorage.removeItem('pendingMessage');
          }
        }
      } catch (error) {
        console.error('Error processing pending message:', error);
        localStorage.removeItem('pendingMessage');
      }
    }, 800);
  }
  ngOnDestroy(): void {
    // Clean up any pending messages if the component is destroyed
    // without being properly processed
    localStorage.removeItem('pendingMessage');
    localStorage.removeItem('startWorkflow');
  }

  switchTab(tab: { label: string }): void {
    if (tab !== this.activeTab) {
      this.tabs.activeTab.set(tab);
      // Load dynamic component if not Editor
      this.setDynamicComponent(tab.label);
    }
  }

  private setDynamicComponent(tabLabel: string): void {
    // Load dynamic component if not Editor
    if (tabLabel === 'USDM') {
      this.dynamicComponent = UsdmComponent;
      this.cdr.detectChanges();
    } else {
      this.dynamicComponent = null;
    }
  }
}
